import {
  type TransitionEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import { MODEL_REGISTRY, type ModelRecord } from '../data/modelRegistry'
import type { ModelTier } from '../domain/types'
import { modelMeetsRouteFloor } from '../engine/modelFit'
import { ProviderIcon } from './ProviderIcon'
import { sameModel } from './modelPreference'

const SEARCH_RESULT_LIMIT = 5

export type ModelSearchMotionInput = 'keyboard' | 'pointer'

function routeLabel(tier: ModelTier): string {
  if (tier === 'direct') return 'Direct'
  if (tier === 'standard') return 'Standard'
  return 'Deep'
}

export function ModelSearch({
  id,
  open,
  motionInput,
  routeTier,
  pinnedModel,
  pinnedProvider,
  onTogglePin,
  onTransitionEnd,
  onToggleProvider,
}: {
  id: string
  open: boolean
  motionInput: ModelSearchMotionInput
  routeTier: ModelTier
  pinnedModel: ModelRecord | null
  pinnedProvider: string | null
  onTogglePin: (model: ModelRecord) => void
  onTransitionEnd: (event: TransitionEvent<HTMLDivElement>) => void
  onToggleProvider: (provider: string) => void
}) {
  const [query, setQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchInputId = useId()
  const searchHelpId = useId()
  const normalizedQuery = query.trim().toLocaleLowerCase()
  const providers = useMemo(
    () => [...new Set(MODEL_REGISTRY.map((model) => model.provider))],
    [],
  )
  const matchingModels = useMemo(() => {
    if (!normalizedQuery) return []

    return MODEL_REGISTRY.filter((model) =>
      `${model.provider} ${model.name} ${model.modelId}`
        .toLocaleLowerCase()
        .includes(normalizedQuery),
    ).slice(0, SEARCH_RESULT_LIMIT)
  }, [normalizedQuery])

  useEffect(() => {
    if (open) searchInputRef.current?.focus()
  }, [open])

  return (
    <div
      className="model-search"
      id={id}
      data-state={open ? 'open' : 'closed'}
      data-motion-input={motionInput}
      aria-hidden={open ? undefined : true}
      inert={!open}
      onTransitionEnd={onTransitionEnd}
    >
      <div className="model-search__clip">
        <div className="model-search__content">
          <section
            className="provider-preference"
            aria-labelledby={`${id}-provider-title`}
          >
            <h3 id={`${id}-provider-title`}>Prefer a provider</h3>
            <p>
              Its closest model appears first. Pinning a provider replaces a
              pinned model.
            </p>
            <ul aria-label="Model providers">
              {providers.map((provider) => {
                const pinned = pinnedProvider === provider
                return (
                  <li key={provider}>
                    <button
                      type="button"
                      aria-pressed={pinned}
                      aria-label={`${
                        pinned ? 'Unpin' : 'Pin'
                      } ${provider} provider`}
                      onClick={() => onToggleProvider(provider)}
                    >
                      <ProviderIcon provider={provider} modelName={provider} />
                      <span>{provider}</span>
                      <span aria-hidden="true">{pinned ? '★' : '☆'}</span>
                    </button>
                  </li>
                )
              })}
            </ul>
            <a href="/preferences/share">
              Share this preference with You Might Not Need Frontier
            </a>
          </section>

          <label htmlFor={searchInputId}>Search provider or model</label>
          <input
            ref={searchInputRef}
            id={searchInputId}
            type="search"
            value={query}
            aria-describedby={searchHelpId}
            onChange={(event) => setQuery(event.target.value)}
          />
          <p id={searchHelpId}>
            Yes means this model is in the {routeLabel(routeTier)} class or
            higher.
          </p>

          {normalizedQuery ? (
            matchingModels.length > 0 ? (
              <ul className="model-search-results" aria-label="Matching models">
                {matchingModels.map((model) => {
                  const fits = modelMeetsRouteFloor(model.tier, routeTier)
                  const pinned = sameModel(pinnedModel, model)

                  return (
                    <li key={`${model.provider}:${model.modelId}`}>
                      <span className="model-search-result__identity">
                        <ProviderIcon
                          provider={model.provider}
                          modelName={model.name}
                          className="provider-icon--search"
                        />
                        <span className="model-search-result__name">
                          <strong>{model.name}</strong>
                          <small>{model.provider}</small>
                        </span>
                      </span>
                      <span
                        className={`model-verdict model-verdict--${
                          fits ? 'yes' : 'no'
                        }`}
                      >
                        {fits ? 'Yes' : 'No'}
                      </span>
                      <button
                        type="button"
                        aria-pressed={pinned}
                        aria-label={`${pinned ? 'Unpin' : 'Pin'} ${model.name}`}
                        onClick={() => onTogglePin(model)}
                      >
                        <span aria-hidden="true">{pinned ? '★' : '☆'}</span>
                        <span>{pinned ? 'Pinned' : 'Pin'}</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            ) : (
              <p className="model-search__empty">No matching model.</p>
            )
          ) : null}

          <output className="sr-only">
            {normalizedQuery
              ? `${matchingModels.length} matching ${
                  matchingModels.length === 1 ? 'model' : 'models'
                }.`
              : ''}
          </output>
        </div>
      </div>
    </div>
  )
}
